import type { ComponentProps } from "solid-js";
import { Show, For } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { isEmpty } from "lodash-es";
import ClosablePill from "../ClosablePill";
import type { IconName } from "../../icon/Icon";
import { Icon } from "../../icon/Icon";
import type { SelectOption } from "../../../types/Menu";

type MultiSelectTriggerProps<T> = {
  ariaLabel: string;
  hasError: boolean;
  ref: HTMLButtonElement;
  value?: SelectOption<T>[];
  onClear?: (option: SelectOption<T>) => void;
  placeholder?: string;
  arkProps: ComponentProps<"button">;
};

const MultiSelectTrigger = <T,>(props: MultiSelectTriggerProps<T>) => {
  const { t } = useTranslation();

  return (
    <div
      role="group"
      class="relative flex min-h-9.5 w-full rounded-sm border bg-surface-default py-1.5"
      classList={{
        "border-error bg-surface-destructive": props.hasError,
        "border-default": !props.hasError,
      }}
    >
      <button
        ref={props.ref}
        aria-label={props.ariaLabel}
        class="absolute inset-y-0 left-0 size-full rounded-sm outline-offset-1 focus-visible:outline-default"
        {...props.arkProps}
      >
        <Show when={isEmpty(props.value)}>
          <span class="flex px-2 text-base-medium text-placeholder">
            {props.placeholder ?? t(($) => $.SELECT_MULTIPLE_PLACEHOLDER)}
          </span>
        </Show>
      </button>
      <Show when={!isEmpty(props.value)}>
        <div class="pointer-events-none flex min-w-0 flex-wrap items-center gap-1 pr-8 pl-1.5">
          <For each={props.value}>
            {(option) => {
              const icon = (): IconName | undefined => {
                if (option.variant === "dynamicValue") {
                  return option.icon?.name;
                } else if (option.variant === "avatar") {
                  return option.shape === "square" ? "users" : "user";
                }
                return undefined;
              };

              return (
                <ClosablePill
                  variant={option.orphaned === true ? "destructive" : "neutral"}
                  label={option.label}
                  icon={option.orphaned === true ? "exclamationCircle" : icon()}
                  onClick={() => props.onClear?.(option)}
                  classList={{ "pointer-events-auto z-10": true }}
                />
              );
            }}
          </For>
        </div>
      </Show>
      <div class="pointer-events-none absolute top-0 right-0 flex h-9 items-center justify-center pr-1">
        <div class="flex size-7 items-center justify-center">
          <Icon type="solid" name="chevronDown" size="md" class="shrink-0 icon-secondary" />
        </div>
      </div>
    </div>
  );
};

export default MultiSelectTrigger;
